import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/Card';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { modalities, engineSteps, engineMetrics } from '../data/engine';
import { pwaBenefits } from '../data/pwaBenefits';
export function FeaturesSection() {
  const revealRef = useScrollReveal();
  const barsRef = useRef<HTMLDivElement>(null);
  const [barsVisible, setBarsVisible] = useState(false);
  useEffect(() => {
    const el = barsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setBarsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <section id="technologia" ref={revealRef} aria-labelledby="technologia-nadpis" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-79" data-mp="17iqc7gmm70rq">
      {/* Intro */}
      <div className="max-w-3xl mx-auto text-center mb-16 md:mb-24" data-id="element-80" data-mp="31ae8h9upxohw">
        <p className="reveal text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-4" data-id="element-81" data-mp="36a8zreieog3h">
          Technológia
        </p>
        <h2 id="technologia-nadpis" className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-tight text-apple-black mb-5" data-id="element-82" data-mp="3b2jwy9lrhnsc">
          Gemini Multimodal Embeddings 2
        </h2>
        <p className="reveal reveal-delay-2 text-lg md:text-xl text-apple-gray leading-relaxed mb-12" data-id="element-83" data-mp="2iexuj4o9sp72">
          Jedna AI, štyri modality. Vidí, počuje, číta a pozerá naraz — my z nej staviame reálne produkty.
        </p>
        <div className="reveal-scale reveal-delay-3 w-full rounded-2xl md:rounded-[2rem] overflow-hidden shadow-xl" data-id="element-84" data-mp="2x9kkkgha09p0">
          <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1000&h=420&fit=crop&crop=center" alt="Abstraktná vizualizácia neurónovej siete" loading="lazy" decoding="async" className="w-full h-44 md:h-80 object-cover" data-id="element-85" data-mp="7lbi7ymbz41u" />
        </div>
      </div>

      {/* Modality diagram */}
      <div className="reveal relative max-w-3xl mx-auto md:h-[400px] mb-20 md:mb-28 flex flex-col md:block items-center gap-6" data-id="element-86" data-mp="2hnfvyf9hdn4v">
        <div className="relative md:absolute md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-20 w-28 h-28 md:w-36 md:h-36 bg-white rounded-full shadow-xl flex items-center justify-center border-4 border-gray-50" data-id="element-87" data-mp="2dlkw7f777ht7">
          <div className="absolute inset-0 rounded-full bg-apple-blue/15 animate-pulse" aria-hidden="true" data-id="element-88" data-mp="1kyqgevlbt6m2" />
          <span className="text-2xl md:text-3xl font-extrabold text-apple-black relative z-10 tracking-tighter" data-id="element-89" data-mp="1ypv89bigculu">AI</span>
        </div>

        <div className="hidden md:flex absolute inset-0 items-center justify-center z-0" aria-hidden="true" data-id="element-90" data-mp="16810xh0u15ce">
          <div className="w-full h-full max-w-[400px] max-h-[400px] border-2 border-dashed border-gray-200 rounded-full rotate-45" data-id="element-91" data-mp="1277deoraxsk1" />
          <div className="absolute w-full h-full max-w-[400px] max-h-[400px] border-2 border-dashed border-gray-200 rounded-full -rotate-45" data-id="element-92" data-mp="s1z2qms1uhr0" />
        </div>

        <ul className="grid grid-cols-2 gap-3 w-full max-w-sm md:max-w-none md:block" aria-label="Podporované modality" data-id="element-93" data-mp="i4ta3a3seioo">
          {modalities.map(m => <li key={m.label} className={`relative md:absolute ${m.positionClass} w-full md:w-32 h-28 md:h-32 bg-white rounded-2xl shadow-md border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden`} data-id="element-94" data-mp="n3xkg42g6mgl">
              <div className={`${m.glowClass} w-full h-12 absolute top-0`} aria-hidden="true" data-id="element-95" data-mp="10u11mgmgzpz6" />
              <m.icon className={`w-7 h-7 ${m.iconClass} mb-2 relative z-10`} aria-hidden="true" data-id="element-96" data-mp="15t469zyjfu6m" />
              <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-97" data-mp="3lk8hs4op0g6n">{m.label}</span>
            </li>)}
        </ul>
      </div>

      {/* How it works */}
      <div className="relative mb-20 md:mb-28" data-id="element-98" data-mp="amn0vnb7jeyj">
      <div className="hidden md:block absolute top-8 left-[16%] right-[16%] border-t-2 border-dashed border-gray-200 z-0" aria-hidden="true" data-id="element-99" data-mp="anofu2vcgllj" />
      <ol className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 relative" data-id="element-100" data-mp="w24m6j0amis">
        {engineSteps.map((step, i) => <li key={step.num} className={`reveal reveal-delay-${i + 1} flex flex-col items-center text-center relative z-10`} data-id="element-101" data-mp="3s3edly9qjtnm" data-mpi={i}>
            <div className="w-16 h-16 bg-white rounded-full shadow-md border border-gray-100 flex items-center justify-center mb-5" data-id="element-102" data-mp="tkh75tkybcd8">
              <span className="text-xl font-extrabold text-apple-gray" data-id="element-103" data-mp="jmtagy3sbusf">{step.num}</span>
            </div>
            <step.icon className="w-7 h-7 text-apple-blue mb-3" aria-hidden="true" data-id="element-104" data-mp="enqfk6hir4y6" />
            <h3 className="text-xl md:text-2xl font-bold text-apple-black mb-2" data-id="element-105" data-mp="eq2usbvw717j">{step.title}</h3>
            <p className="text-apple-gray leading-relaxed max-w-xs" data-id="element-106" data-mp="34ao1np1u1adc">{step.desc}</p>
          </li>)}
      </ol>
      </div>

      {/* Metrics */}
      <div ref={barsRef} className="reveal max-w-3xl mx-auto mb-20 md:mb-32" data-id="element-107" data-mp="1ewy678xz9whc">
        <h3 className="text-2xl md:text-4xl font-bold text-center text-apple-black mb-10 md:mb-14 tracking-tight" data-id="element-108" data-mp="1jugkzfyof5x9">
          Výkon v číslach
        </h3>
        <div className="space-y-7" data-id="element-109" data-mp="1osrprca46q8m">
          {engineMetrics.map((bar, index) => <div key={bar.label} data-id="element-110" data-mp="1tmdvekhv1tux" data-mpi={index}>
              <div className="flex justify-between items-end mb-2.5" data-id="element-111" data-mp="1yjw8mn2wuv8q">
                <span className="text-base font-semibold text-apple-black" data-id="element-112" data-mp="1yjmhuxa1kk1z">{bar.label}</span>
                <span className="text-lg font-bold text-apple-blue" data-id="element-113" data-mp="22jwo8b1qxchd">{bar.value} %</span>
              </div>
              <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-label={bar.label} aria-valuenow={bar.value} aria-valuemin={0} aria-valuemax={100} data-id="element-114" data-mp="27ldihnpq60oz">
                <div className="h-full bg-apple-blue rounded-full" style={{
              width: barsVisible ? `${bar.value}%` : '0%',
              transition: `width 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.12}s`
            }} data-id="element-115" data-mp="i7qk9ztg5lcw" />
              </div>
            </div>)}
        </div>
      </div>

      {/* PWA */}
      <div id="pwa" className="pt-16 md:pt-20 border-t border-gray-200" data-id="element-116" data-mp="f647vightdso">
        <div className="reveal text-center mb-12 md:mb-16 max-w-3xl mx-auto" data-id="element-117" data-mp="t2hokpd7db63">
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-tight text-apple-black mb-5" data-id="element-118" data-mp="p30iputv6c0e">
            Prečo PWA? Budúcnosť je v prehliadači.
          </h2>
          <p className="text-lg md:text-xl text-apple-gray leading-relaxed" data-id="element-119" data-mp="xusse3b2dh3x">
            Jedna aplikácia pre web, iOS aj Android — bez App Storu a za zlomok ceny.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-120" data-mp="17sow4a1o99s0">
          {pwaBenefits.map((b, i) => <div key={b.title} className={`reveal reveal-delay-${i + 1}`} data-id="element-121" data-mp="1rdyzqmeaqqs4" data-mpi={i}>
              <Card className="h-full border border-gray-200 rounded-3xl bg-white shadow-sm overflow-hidden" data-id="element-122" data-mp="1vg3p38xpp18y">
                <img src={b.image} alt={b.imageAlt} loading="lazy" decoding="async" className="w-full h-40 object-cover" data-id="element-123" data-mp="1hgkt1ei6dgd0" />
                <CardContent className="p-6 md:p-8 text-center flex flex-col items-center" data-id="element-124" data-mp="17pgf8i48k1jr">
                  <div className={`w-14 h-14 ${b.iconBg} rounded-full flex items-center justify-center mb-5 -mt-12 relative z-10 border-4 border-white shadow-md`} data-id="element-125" data-mp="1chhlnneq2y1v">
                    <b.icon className={`w-7 h-7 ${b.iconColor}`} aria-hidden="true" data-id="element-126" data-mp="13oxl2hevejee" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-apple-black mb-3" data-id="element-127" data-mp="orx3v7ub26u6">{b.title}</h3>
                  <p className="text-apple-gray leading-relaxed" data-id="element-128" data-mp="tqgpf74zlgm0">{b.desc}</p>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}